import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as UserActions from './user.actions';
import {
  catchError,
  EMPTY,
  finalize,
  from,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { PageLoaderService } from '../../../util/pageloader.service';
import { UserService } from './users.service';
import { HttpErrorResponse } from '@angular/common/http';
import { StorageService } from '../../../util/storage.service';
import { CONST } from '../../../util/const';
import { Router } from '@angular/router';
import { FaroService } from '../../../util/faro.service';
import {
  isEmail,
  UserProfileRequestDTO,
  UserResponseDTO,
  UserRoles,
  UserStatus,
} from './models';
import { ToastService } from '@black/ui';
import { isValidMobileNumber } from '../../../util/form.validators';
import { AuthService } from '../../../services/auth.service';
import { AuthResponse } from './models';

@Injectable()
export class UserEffects {
  private actions$ = inject(Actions);
  private pageLoaderService = inject(PageLoaderService);
  private userService = inject(UserService);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private faroService = inject(FaroService);
  private authService = inject(AuthService);

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.login.type),
      switchMap((action: any) => {
        this.pageLoaderService.open();
        return this.userService.login(action.credentials).pipe(
          map((response) =>
            UserActions.loginSuccess({
              response,
            }),
          ),
          catchError((err: HttpErrorResponse) => {
            this.faroService.captureError(err);
            return of(UserActions.loginFailed({ err }));
          }),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    );
  });

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.loginSuccess.type),
        switchMap(
          (action: {
            response: {
              token: string;
              refreshToken: string;
              user: UserResponseDTO;
            };
          }) => {
            const { token, refreshToken, user } = action.response;

            // Track user login in Faro
            this.faroService.trackUserLogin({
              id: user.username,
              email: isEmail(user.username) ? user.username : 'Unknown',
              name: `${user.name.firstName} ${user.name.lastName}`,
              role: user.role,
              mobile: isValidMobileNumber(user.username)
                ? user.username
                : 'Unknown',
            });

            // Use AuthService to set authentication state with refresh token
            this.authService.setAuthState(token, refreshToken);

            if (user.status === UserStatus.UNVERIFIED) {
              this.router.navigate(['/auth/verify-user']);
            } else if (user.status === UserStatus.PENDING_PARENTAL_CONSENT) {
              this.router.navigate(['/auth/pending-consent']);
            } else if (user.role === UserRoles.ADMIN) {
              this.router.navigate(['/admin']);
            } else {
              this.storageService
                .get(CONST.STORAGE_KEYS.RETURN_URL)
                .then((deeplinkUrl) => {
                  if (deeplinkUrl) {
                    this.storageService.remove(CONST.STORAGE_KEYS.RETURN_URL);
                    const sanitizedUrl = deeplinkUrl.split('#')[0];
                    this.router.navigate([sanitizedUrl]);
                  } else {
                    this.router.navigate(['/dashboard/overview']);
                  }
                });
            }
            return EMPTY;
          },
        ),
      ),
    { dispatch: false },
  );

  sendVerificationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.sendVerificationCode),
      switchMap((action: { username?: string }) => {
        this.pageLoaderService.open();
        return this.userService.sendVerificationCode(action.username).pipe(
          switchMap(() => {
            return of(UserActions.sendVerificationCodeSuccess());
          }),
          catchError((err) => {
            return of(UserActions.sendVerificationCodeFailed({ err: err }));
          }),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    ),
  );

  // Registration v2: Step 1 Identify
  registerIdentify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.registerIdentify),
      switchMap((action: { username: string }) => {
        this.pageLoaderService.open();
        return this.userService.registerIdentify(action.username).pipe(
          switchMap(() => of(UserActions.registerIdentifySuccess())),
          catchError((err) => of(UserActions.registerIdentifyFailed({ err }))),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    ),
  );

  // Registration v2: Step 2 Verify
  registerVerify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.registerVerify),
      switchMap((action: { username: string; code: string }) => {
        this.pageLoaderService.open();
        return this.userService
          .registerVerify(action.username, action.code)
          .pipe(
            switchMap((response: { registrationToken: string }) =>
              of(UserActions.registerVerifySuccess(response)),
            ),
            catchError((err) => of(UserActions.registerVerifyFailed({ err }))),
            finalize(() => this.pageLoaderService.close()),
          );
      }),
    ),
  );

  // Registration v2: Step 3 Complete
  registerComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.registerComplete),
      switchMap(
        (action: {
          token: string;
          password: string;
          firstName: string;
          lastName: string;
          role: string;
          dateOfBirth?: Date;
          parentEmail?: string;
          inviteToken?: string;
        }) => {
          this.pageLoaderService.open();
          return this.userService
            .registerComplete({
              token: action.token,
              password: action.password,
              firstName: action.firstName,
              lastName: action.lastName,
              role: action.role,
              dateOfBirth: action.dateOfBirth,
              parentEmail: action.parentEmail,
              inviteToken: action.inviteToken,
            })
            .pipe(
              switchMap(
                (response: {
                  token?: string;
                  refreshToken?: string;
                  user?: UserResponseDTO;
                  message?: string;
                }) => {
                  if (response.message) {
                    this.toastService.showToast(response.message, 'success');
                    this.router.navigate(['/auth/login']);
                    return of(
                      UserActions.registerCompleteSuccess({
                        message: response.message,
                      }),
                    );
                  } else if (
                    response.token &&
                    response.refreshToken &&
                    response.user
                  ) {
                    const authResponse: AuthResponse = {
                      token: response.token,
                      refreshToken: response.refreshToken,
                      user: response.user,
                    };
                    return of(
                      UserActions.registerCompleteSuccess({
                        response: authResponse,
                      }),
                      UserActions.loginSuccess({ response: authResponse }),
                    );
                  }
                  return EMPTY;
                },
              ),
              catchError((err) =>
                of(UserActions.registerCompleteFailed({ err })),
              ),
              finalize(() => this.pageLoaderService.close()),
            );
        },
      ),
    ),
  );

  verifyUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.verifyUser.type),
      switchMap((payload: any) => {
        this.pageLoaderService.open();
        return this.userService.verifyUserWithOtp(payload.code).pipe(
          map(({ success }) => {
            if (success) {
              this.router.navigate(['/live-classes']);
              this.toastService.showToast(
                'User verified successfully',
                'success',
              );
              return UserActions.userVerificationSuccess();
            } else {
              return UserActions.userVerificationFailed({
                err: 'Invalid or expired otp',
              });
            }
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              UserActions.userVerificationFailed({
                err: error.error?.description,
              }),
            );
          }),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    ),
  );

  sendResetPasswordRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.sendResetPasswordRequest.type),
      switchMap((action: { username: string; code: string }) => {
        this.pageLoaderService.open();
        return this.userService
          .requestPasswordResetTokenWithOtp(action.username, action.code)
          .pipe(
            switchMap((res) =>
              of(
                UserActions.resetPasswordRequestSuccess({
                  resetToken: res.resetToken,
                }),
              ),
            ),
            catchError((err) =>
              of(UserActions.resetPasswordRequestFailed({ err: err })),
            ),
            finalize(() => this.pageLoaderService.close()),
          );
      }),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.resetPassword.type),
      switchMap((action: { token: string; password: string }) => {
        this.pageLoaderService.open();
        return this.userService
          .resetPassword(action.token, action.password)
          .pipe(
            switchMap((res) => of(UserActions.resetPasswordSuccess())),
            catchError((err) =>
              of(UserActions.resetPasswordFailed({ err: err })),
            ),
            finalize(() => this.pageLoaderService.close()),
          );
      }),
    ),
  );

  updateUserProfile = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUserProfile.type),
      switchMap(
        (action: {
          userProfile: UserProfileRequestDTO;
          redirectOnSuccess: boolean;
        }) => {
          return this.userService.updateUserProfile(action.userProfile).pipe(
            switchMap((results) =>
              of(
                UserActions.updateUserProfileSuccess({
                  user: results,
                  redirect: action.redirectOnSuccess,
                }),
              ),
            ),
            catchError(() => EMPTY),
          );
        },
      ),
    ),
  );

  updateUserProfileSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.updateUserProfileSuccess.type),
        switchMap((action: { user: UserResponseDTO; redirect: boolean }) => {
          if (action.redirect) {
            this.storageService
              .get(CONST.STORAGE_KEYS.RETURN_URL)
              .then((deeplinkUrl) => {
                if (deeplinkUrl) {
                  this.storageService.remove(CONST.STORAGE_KEYS.RETURN_URL);
                  this.router.navigate([deeplinkUrl]);
                } else {
                  this.router.navigate(['live-classes']);
                }
              });
          }
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  register$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.register.type),
      switchMap((action: any) => {
        this.pageLoaderService.open();
        return this.userService.register(action.user).pipe(
          map((response) =>
            UserActions.registerSuccess({
              user: response,
              autoLogin: true,
              loginCredentials: {
                username: action.user.username,
                password: action.user.password,
              },
            }),
          ),
          catchError((err) => {
            return of(UserActions.registerFailed({ err }));
          }),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    );
  });

  registerSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.registerSuccess.type),
      switchMap((action: any) => {
        if (action.autoLogin) {
          return of(
            UserActions.login({
              credentials: {
                username: action.loginCredentials.username,
                password: action.loginCredentials.password,
              },
            }),
          );
        }
        return of();
      }),
      finalize(() => this.pageLoaderService.close()),
    );
  });

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.logout.type),
        tap((action: { redirect: boolean }) => {
          // Track user logout in Faro
          this.faroService.trackUserLogout();

          // Use AuthService to clear authentication state
          this.authService.clearAuthState();

          // Remove additional tokens if needed
          this.storageService.remove(CONST.STORAGE_KEYS.ZOOM_ACCESS_TOKEN);
          this.storageService.remove(CONST.STORAGE_KEYS.ZOOM_REFRESH_TOKEN);

          if (action.redirect) {
            this.router.navigate(['/auth/login']);
          }
        }),
      ),
    { dispatch: false },
  );

  deleteUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteUserAccount),
      switchMap(() => {
        this.pageLoaderService.open();
        return this.userService.deleteAccount().pipe(
          switchMap(() => {
            this.toastService.showToast(
              'Your account was deleted successfully.',
              'success',
            );
            return of(
              UserActions.deleteUserAccountSuccess(),
              UserActions.logout({ redirect: true }),
            );
          }),
          catchError((err: HttpErrorResponse) => {
            this.faroService.captureError(err);
            this.toastService.showToast(
              err?.error?.message ||
                'Failed to delete account. Please try again.',
              'error',
            );
            return of(UserActions.deleteUserAccountFailed({ err }));
          }),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    ),
  );

  requestImpersonation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.requestImpersonation),
      switchMap(({ userId }) => {
        this.pageLoaderService.open();
        return this.userService.requestImpersonation(userId).pipe(
          switchMap(() => of(UserActions.requestImpersonationSuccess())),
          catchError((err: HttpErrorResponse) =>
            of(UserActions.requestImpersonationFailed({ err })),
          ),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    ),
  );

  verifyImpersonation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.verifyImpersonation),
      switchMap(({ userId, otp }) => {
        this.pageLoaderService.open();
        return this.userService.verifyImpersonation(userId, otp).pipe(
          switchMap((response) =>
            from(
              Promise.all([
                this.storageService.get(CONST.STORAGE_KEYS.ACCESS_TOKEN),
                this.storageService.get(CONST.STORAGE_KEYS.REFRESH_TOKEN),
              ]),
            ).pipe(
              switchMap(([adminToken, adminRefreshToken]) => {
                // Persist admin tokens before overwriting with impersonation token
                if (adminToken) {
                  this.storageService.set(
                    CONST.STORAGE_KEYS.IMPERSONATION_ADMIN_TOKEN,
                    adminToken,
                  );
                }
                if (adminRefreshToken) {
                  this.storageService.set(
                    CONST.STORAGE_KEYS.IMPERSONATION_ADMIN_REFRESH_TOKEN,
                    adminRefreshToken,
                  );
                }
                // Set impersonation token silently (no toast, no refresh token)
                this.authService.setAuthState(response.token, undefined, true);
                // Remove refresh token so the interceptor cannot silently extend the session
                this.storageService.remove(CONST.STORAGE_KEYS.REFRESH_TOKEN);
                this.router.navigate(['/live-classes']);
                return of(
                  UserActions.verifyImpersonationSuccess({
                    token: response.token,
                    user: response.user,
                  }),
                );
              }),
            ),
          ),
          catchError((err: HttpErrorResponse) =>
            of(UserActions.verifyImpersonationFailed({ err })),
          ),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    ),
  );

  endImpersonation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.endImpersonation),
      switchMap(() =>
        from(
          Promise.all([
            this.storageService.get(
              CONST.STORAGE_KEYS.IMPERSONATION_ADMIN_TOKEN,
            ),
            this.storageService.get(
              CONST.STORAGE_KEYS.IMPERSONATION_ADMIN_REFRESH_TOKEN,
            ),
          ]),
        ).pipe(
          switchMap(([adminToken, adminRefreshToken]) => {
            if (!adminToken) {
              // No admin token to restore — fall back to full logout
              return of(UserActions.logout({ redirect: true }));
            }
            // Restore admin auth state silently
            this.authService.setAuthState(
              adminToken,
              adminRefreshToken || undefined,
              true,
            );
            // Clear impersonation tokens
            this.storageService.remove(
              CONST.STORAGE_KEYS.IMPERSONATION_ADMIN_TOKEN,
            );
            this.storageService.remove(
              CONST.STORAGE_KEYS.IMPERSONATION_ADMIN_REFRESH_TOKEN,
            );
            // Fetch the restored admin's user info
            return this.userService.getUserInfo().pipe(
              switchMap((user) => {
                this.router.navigate(['/admin/users']);
                return of(UserActions.endImpersonationSuccess({ user }));
              }),
              catchError(() => {
                this.router.navigate(['/admin/users']);
                return of(UserActions.endImpersonationSuccess({ user: null }));
              }),
            );
          }),
        ),
      ),
    ),
  );

  loadTutors$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadTutors.type),
      switchMap((action: any) => {
        return this.userService
          .getPublicTutors(action.page, action.limit, action.filters)
          .pipe(
            map((response) => UserActions.loadTutorsSuccess({ response })),
            catchError((err: HttpErrorResponse) => {
              this.faroService.captureError(err);
              return of(UserActions.loadTutorsFailed({ err }));
            }),
          );
      }),
    );
  });

  loadTutorProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadTutorProfile.type),
      switchMap((action: any) => {
        this.pageLoaderService.open();
        return this.userService.getPublicTutorProfile(action.identifier).pipe(
          map((response) => UserActions.loadTutorProfileSuccess({ response })),
          catchError((err: HttpErrorResponse) => {
            this.faroService.captureError(err);
            return of(UserActions.loadTutorProfileFailed({ err }));
          }),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    );
  });

  updateTutorProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateTutorProfile.type),
      switchMap((action: any) => {
        this.pageLoaderService.open();
        return this.userService.updateTutorProfile(action.tutorInfo).pipe(
          map((user) => {
            this.toastService.showToast(
              'Profile updated successfully!',
              'success',
            );
            return UserActions.updateTutorProfileSuccess({ user });
          }),
          catchError((err: HttpErrorResponse) => {
            this.faroService.captureError(err);
            this.toastService.showToast(
              err.error?.message || 'Failed to update profile',
              'error',
            );
            return of(UserActions.updateTutorProfileFailed({ err }));
          }),
          finalize(() => this.pageLoaderService.close()),
        );
      }),
    );
  });
}
